/**
 * Copyright © 2018 Emu Analytics
 */

import Axios, { AxiosError, AxiosInstance } from 'axios';
import Debug from 'debug';
import { IConfig } from './api-interfaces';
import { ApiKeyEndpoints } from './apikey.endpoints';
import { AppConfigEndpoints } from './appconfig.endpoints';
import { ApplicationEndpoints } from './application.endpoints';
import { AuthEndpoints } from './auth.edpoints';
import { ContactPointEndpoints } from './contact-point.endpoints';
import { DatabaseEndpoints } from './database.endpoints';
import { DatasetEndpoints } from './dataset.endpoints';
import { FlowEngineError } from './errors';
import { JobEndpoints } from './job.endpoints';
import { MapEndpoints } from './map.endoints';
import { MapLayerEndpoints } from './maplayer.endpoints';
import { MediaEndpoints } from './media.endpoints';
import { PipelineEndpoints } from './pipeline.endpoints';
import { StyleEndpoints } from './style.endpoint';
import { TileSourceEndpoints } from './tile-source.endpoints';
import { TileEndpoints } from './tile.endpoints';
import { UploadEndpoints } from './upload.endpoints';
import {
  WebSocketGateway,
  WebSocketManagerOptions,
  WebSocketSubscribeCallback,
  WebSocketUnsubscribeCallback,
  WebSocketUnsubscriber
} from './web-socket-gateway';

export type ClientOptions = WebSocketManagerOptions;

export class Client {
  private readonly axios: AxiosInstance;
  readonly debug = Debug('flow-engine-client');

  public webSocketGateway: WebSocketGateway;

  accessToken: string | null = null;

  auth = new AuthEndpoints(this);
  tiles = new TileEndpoints(this);
  maps = new MapEndpoints(this);
  apiKeys = new ApiKeyEndpoints(this);
  applications = new ApplicationEndpoints(this);
  appconfig = new AppConfigEndpoints(this);
  contactpoints = new ContactPointEndpoints(this);
  databases = new DatabaseEndpoints(this);
  datasets = new DatasetEndpoints(this);
  styles = new StyleEndpoints(this);
  tileSources = new TileSourceEndpoints(this);
  uploads = new UploadEndpoints(this);
  jobs = new JobEndpoints(this);
  maplayers = new MapLayerEndpoints(this);
  media = new MediaEndpoints(this);
  pipelines = new PipelineEndpoints(this);

  useRequestInterceptor(interceptor: (config: any) => any) {
    return this.axios.interceptors.request.use(interceptor);
  }

  ejectRequestInterceptor(id: number) {
    this.axios.interceptors.request.eject(id);
  }

  constructor(public baseUrl: string, public readonly apiKey: string | null = null, options?: ClientOptions) {
    this.axios = Axios.create({
      baseURL: baseUrl,
      maxContentLength: 524288000,
      transformResponse: [
        function transformResponse(data, headers) {
          if (headers?.['content-type']?.startsWith('application/json')) {
            // Force parsing of data if expected content type is JSON
            // This ensures that errors are thrown if JSON is truncated due to a query timeout
            // (default Axios behavior is simply to return a string if JSON parsing fails)
            return JSON.parse(data);
          }

          return data;
        }
      ]
    });

    this.webSocketGateway = new WebSocketGateway(this, options);
  }

  connect(): Promise<void> {
    return this.webSocketGateway.connect();
  }

  disconnect(): Promise<void> {
    return this.webSocketGateway.disconnect();
  }

  config(): Promise<IConfig> {
    return this._get('/api/v1/config');
  }

  _get(path: string, options: any = {}): Promise<any> {
    // Move apiKey from params
    if (options.params?.apiKey) {
      options.apiKey = options.params.apiKey;
      delete options.params.apiKey;
    }

    return this.axios({
      method: 'get',
      url: path,
      ...options,
      ...this.headers(options)
    })
      .then(res => res.data)
      .catch(this.handleError);
  }

  _post(path: string, data: object, options: any = {}): Promise<any> {
    // Move apiKey from params
    if (options.params?.apiKey) {
      options.apiKey = options.params.apiKey;
      delete options.params.apiKey;
    }

    return this.axios({
      method: 'post',
      url: path,
      data,
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
      ...options,
      ...this.headers(options)
    })
      .then(res => res.data)
      .catch(this.handleError);
  }

  _put(path: string, data: object, options: any = {}): Promise<any> {
    // Move apiKey from params
    if (options.params?.apiKey) {
      options.apiKey = options.params.apiKey;
      delete options.params.apiKey;
    }

    return this.axios({
      method: 'put',
      url: path,
      data,
      ...options,
      ...this.headers(options)
    })
      .then(res => res.data)
      .catch(this.handleError);
  }

  _delete(path: string, options: any = {}): Promise<any> {
    // Move apiKey from params
    if (options.params?.apiKey) {
      options.apiKey = options.params.apiKey;
      delete options.params.apiKey;
    }

    return this.axios({
      method: 'delete',
      url: path,
      ...options,
      ...this.headers(options)
    })
      .then(res => res.data)
      .catch(this.handleError);
  }

  // Subscribe to named channel - errors and messages are delivered via the callback
  _subscribe(channel: string, cb: WebSocketSubscribeCallback): WebSocketUnsubscriber {
    if (!this.webSocketGateway.isConnected) {
      this.webSocketGateway.connect();
    }

    return this.webSocketGateway.subscribe(channel, cb);
  }

  // Unsubscribe from named channel - optional callback is called on completion
  _unsubscribe(channel: string, cb?: WebSocketUnsubscribeCallback) {
    if (!this.webSocketGateway.isConnected) {
      this.webSocketGateway.connect();
    }
    this.webSocketGateway.unsubscribe(channel, cb);
  }

  /**
   * Normalize errors
   * Throws a new error based on type of input error
   *
   * @param {AxiosError} e
   */
  private handleError(e: AxiosError) {
    // Flow API error
    if (e.response && e.response.data) {
      if (typeof e.response.data === 'object') {
        const data: any = e.response.data;
        throw new FlowEngineError(data.message || data.error, data.error, e.response.status);
      } else if (typeof e.response.data === 'string') {
        throw new FlowEngineError(e.response.data, null, e.response.status);
      }
    }

    // Generic error

    // Don't leak information in error
    const cleanError: any = e;
    delete cleanError.config;
    delete cleanError.request;
    delete cleanError.response;

    throw new FlowEngineError(cleanError.message, cleanError);
  }

  private headers(options: any) {
    const headers = options.headers || {};

    if (options.apiKey || this.apiKey) {
      headers['X-Api-Key'] = options.apiKey || this.apiKey;
    }

    // Add default auth header if token supplied and not already set in options
    if (this.accessToken) {
      headers.Authorization = headers.Authorization || `Bearer ${this.accessToken}`;
    }

    return { headers };
  }
}
